/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { toast } from "../../ui/sonner";
import { UserCheck, Plus, X, Loader2 } from "lucide-react";
import { inp } from "./assetUtils";

/* 报废审批代理人配置（CEO / 管理员）：任意在职账号 + 起止日期（留空 = 长期） */
export function AssetApproversDialog({ zh, onClose, onSaved }) {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/finance/fixed-assets/approvers").then(({ data }) => setRows(data.delegates.map((d) => ({ ...d, from: d.from || "", to: d.to || "" })))).catch(() => setRows([]));
    api.get("/users/directory").then(({ data }) => setUsers(data.filter((u) => u.username && !["admin", "ceo"].includes(u.role)).sort((a, b) => (a.name || a.username).localeCompare(b.name || b.username)))).catch(() => {});
  }, [api]);
  const add = () => {
    if (!pick || rows.some((r) => r.username === pick)) return;
    const u = users.find((x) => x.username === pick);
    setRows((p) => [...p, { username: pick, name: u?.name || u?.nameZh || pick, role: u?.role, from: "", to: "", note: "" }]);
    setPick("");
  };
  const upd = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/finance/fixed-assets/approvers", { delegates: rows });
      toast.success(T(`已保存 ${data.delegates.length} 位代理审批人`, "Saved"));
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl" data-testid="asset-approvers-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-indigo-600" />{T("报废审批 · 代理审批人", "Disposal approval delegates")}</DialogTitle>
        <DialogDescription>{T("CEO 出差期间可指定代理；代理期内代理人收到报废申请铃铛并可批准 / 驳回，审批记录标注「代理」。固定审批人：CEO、IT 管理员。", "Delegates receive disposal requests and may approve / reject while active; decisions are marked as delegated.")}</DialogDescription></DialogHeader>
      {!rows ? <div className="py-6 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <div className="space-y-3">
          <div className="flex-wrap md:flex-nowrap flex gap-2">
            <select className={`${inp} flex-1`} data-testid="asset-approvers-pick" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{T("选择账号…", "Pick a user…")}</option>
              {users.filter((u) => !rows.some((r) => r.username === u.username)).map((u) => <option key={u.username} value={u.username}>{u.name || u.nameZh || u.username}（{u.username} · {u.role}）</option>)}
            </select>
            <Button size="sm" variant="outline" data-testid="asset-approvers-add" disabled={!pick} onClick={add} className="gap-1"><Plus className="h-4 w-4" />{T("添加", "Add")}</Button>
          </div>
          {rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-3" data-testid="asset-approvers-empty">{T("暂无代理审批人（仅 CEO / 管理员可审批）", "No delegates")}</p> : (
            <div className="space-y-2" data-testid="asset-approvers-list">
              {rows.map((r, i) => (
                <div key={r.username} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`asset-approver-${r.username}`}>
                  <div><p className="font-semibold text-slate-800">{r.name}</p><p className="text-[10px] text-slate-400">{r.username} · {r.role}{r.active === false && (r.from || r.to) ? ` · ${T("当前未生效", "inactive")}` : ""}</p></div>
                  <label className="text-[11px] text-slate-500">{T("自", "From")}<input type="date" className="block text-xs rounded-md border border-slate-200 px-2 py-1" data-testid={`asset-approver-from-${r.username}`} value={r.from} onChange={(e) => upd(i, "from", e.target.value)} /></label>
                  <label className="text-[11px] text-slate-500">{T("至", "To")}<input type="date" className="block text-xs rounded-md border border-slate-200 px-2 py-1" data-testid={`asset-approver-to-${r.username}`} value={r.to} onChange={(e) => upd(i, "to", e.target.value)} /></label>
                  <button aria-label={T("移除代理审批人", "Remove delegate")} data-testid={`asset-approver-remove-${r.username}`} onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2"><Button variant="outline" onClick={onClose}>{T("取消", "Cancel")}</Button>
            <Button data-testid="asset-approvers-save" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" onClick={save}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T("保存", "Save")}</Button></div>
        </div>
      )}
    </DialogContent></Dialog>
  );
}
